import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const SettingsDataContext = createContext();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_KEYS = {
  SKILLS: 'settings_cache_skills',
  POSITIONS: 'settings_cache_positions',
  EMAIL_TEMPLATES: 'settings_cache_email_templates',
  PDF_TEMPLATES: 'settings_cache_pdf_templates'
};

// Cache helper functions
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

const clearCache = (key) => {
  if (key) {
    localStorage.removeItem(key);
  } else {
    Object.values(CACHE_KEYS).forEach(k => localStorage.removeItem(k));
  }
};

export const SettingsDataProvider = ({ children }) => {
  const [skills, setSkills] = useState(null);
  const [positions, setPositions] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState(null);
  const [pdfTemplates, setPdfTemplates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch skills
  const fetchSkills = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(CACHE_KEYS.SKILLS);
      if (cached) {
        setSkills(cached);
        return cached;
      }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/skills`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch skills');

      const result = await response.json();
      const data = result.skills || [];

      setSkills(data);
      setCachedData(CACHE_KEYS.SKILLS, data);
      return data;
    } catch (error) {
      console.error('Error fetching skills:', error);
      throw error;
    }
  }, [API_URL]);

  // Fetch positions
  const fetchPositions = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(CACHE_KEYS.POSITIONS);
      if (cached) {
        setPositions(cached);
        return cached;
      }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/positions`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch positions');

      const result = await response.json();
      const data = result.data || [];

      setPositions(data);
      setCachedData(CACHE_KEYS.POSITIONS, data);
      return data;
    } catch (error) {
      console.error('Error fetching positions:', error);
      throw error;
    }
  }, [API_URL]);

  // Fetch email templates
  const fetchEmailTemplates = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(CACHE_KEYS.EMAIL_TEMPLATES);
      if (cached) {
        setEmailTemplates(cached);
        return cached;
      }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/matching/email-templates`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch email templates');

      const result = await response.json();
      const data = result.data || [];

      setEmailTemplates(data);
      setCachedData(CACHE_KEYS.EMAIL_TEMPLATES, data);
      return data;
    } catch (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }
  }, [API_URL]);

  // Fetch PDF templates
  const fetchPdfTemplates = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(CACHE_KEYS.PDF_TEMPLATES);
      if (cached) {
        setPdfTemplates(cached);
        return cached;
      }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/admin/pdf-templates`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch PDF templates');

      const result = await response.json();
      const data = result.data || [];

      setPdfTemplates(data);
      setCachedData(CACHE_KEYS.PDF_TEMPLATES, data);
      return data;
    } catch (error) {
      console.error('Error fetching PDF templates:', error);
      throw error;
    }
  }, [API_URL]);

  // Preload all data in parallel
  const preloadAll = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel for maximum performance
      await Promise.allSettled([
        fetchSkills(forceRefresh),
        fetchPositions(forceRefresh),
        fetchEmailTemplates(forceRefresh),
        fetchPdfTemplates(forceRefresh)
      ]);
    } catch (error) {
      console.error('Error preloading data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [fetchSkills, fetchPositions, fetchEmailTemplates, fetchPdfTemplates]);

  // Invalidate specific cache
  const invalidateCache = useCallback((key) => {
    clearCache(key);

    switch (key) {
      case CACHE_KEYS.SKILLS:
        setSkills(null);
        break;
      case CACHE_KEYS.POSITIONS:
        setPositions(null);
        break;
      case CACHE_KEYS.EMAIL_TEMPLATES:
        setEmailTemplates(null);
        break;
      case CACHE_KEYS.PDF_TEMPLATES:
        setPdfTemplates(null);
        break;
      default:
        // Clear all
        setSkills(null);
        setPositions(null);
        setEmailTemplates(null);
        setPdfTemplates(null);
    }
  }, []);

  // Refresh specific resource
  const refreshSkills = useCallback(() => fetchSkills(true), [fetchSkills]);
  const refreshPositions = useCallback(() => fetchPositions(true), [fetchPositions]);
  const refreshEmailTemplates = useCallback(() => fetchEmailTemplates(true), [fetchEmailTemplates]);
  const refreshPdfTemplates = useCallback(() => fetchPdfTemplates(true), [fetchPdfTemplates]);

  const value = {
    // Data
    skills,
    positions,
    emailTemplates,
    pdfTemplates,

    // State
    loading,
    error,

    // Fetch methods
    fetchSkills,
    fetchPositions,
    fetchEmailTemplates,
    fetchPdfTemplates,
    preloadAll,

    // Refresh methods
    refreshSkills,
    refreshPositions,
    refreshEmailTemplates,
    refreshPdfTemplates,

    // Cache management
    invalidateCache,
    cacheKeys: CACHE_KEYS
  };

  return (
    <SettingsDataContext.Provider value={value}>
      {children}
    </SettingsDataContext.Provider>
  );
};

export const useSettingsData = () => {
  const context = useContext(SettingsDataContext);
  if (!context) {
    throw new Error('useSettingsData must be used within SettingsDataProvider');
  }
  return context;
};
